/**
 * GazetteSectionTypeManagement — Admin CRUD types de sections gazette
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, GripVertical, Loader, ArrowUp, ArrowDown, Lock } from 'lucide-react';
import {
  getGazetteSectionTypes, createGazetteSectionType,
  updateGazetteSectionType, deleteGazetteSectionType, reorderGazetteSectionTypes
} from '../../api';
import { useToast } from '../../contexts/NotificationContext';

export default function GazetteSectionTypeManagement() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', emoji: '', description: '' });
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getGazetteSectionTypes();
      setTypes(data);
    } catch (err) {
      toast.error('Erreur chargement types de sections');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Le nom est requis');
    try {
      await createGazetteSectionType(form);
      setForm({ name: '', emoji: '', description: '' });
      setShowAdd(false);
      toast.success('Type de section créé');
      load();
    } catch (err) {
      toast.error('Erreur création');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await updateGazetteSectionType(id, form);
      setEditingId(null);
      toast.success('Type mis à jour');
      load();
    } catch (err) {
      toast.error('Erreur mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type de section ?')) return;
    try {
      await deleteGazetteSectionType(id);
      toast.success('Type supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    }
  };

  const handleMove = async (index, direction) => {
    const newTypes = [...types];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newTypes.length) return;
    [newTypes[index], newTypes[swapIdx]] = [newTypes[swapIdx], newTypes[index]];
    setTypes(newTypes);

    try {
      await reorderGazetteSectionTypes(newTypes.map((t, i) => ({ id: t.id, position: i })));
    } catch (err) {
      toast.error('Erreur réorganisation');
      load();
    }
  };

  const startEdit = (type) => {
    setEditingId(type.id);
    setForm({ name: type.name, emoji: type.emoji || '', description: type.description || '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Loader className="animate-spin mr-2" size={18} />
        Chargement...
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Types de sections gazette</h2>
        <button
          onClick={() => { setShowAdd(!showAdd); setForm({ name: '', emoji: '', description: '' }); }}
          className="flex items-center gap-1.5 bg-primary text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-dark"
        >
          <Plus size={14} /> Ajouter
        </button>
      </div>

      {/* Formulaire ajout */}
      {showAdd && (
        <div className="bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 space-y-3">
          <div className="grid grid-cols-12 gap-3">
            <input
              type="text"
              placeholder="Emoji"
              value={form.emoji}
              onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))}
              className="col-span-2 border rounded-lg px-3 py-1.5 text-sm text-center"
              maxLength={4}
            />
            <input
              type="text"
              placeholder="Nom de la section"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="col-span-4 border rounded-lg px-3 py-1.5 text-sm"
            />
            <input
              type="text"
              placeholder="Description"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="col-span-4 border rounded-lg px-3 py-1.5 text-sm"
            />
            <div className="col-span-2 flex gap-2">
              <button onClick={handleCreate} className="bg-primary text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-dark">
                Créer
              </button>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600 text-sm">
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="space-y-2">
        {types.map((type, idx) => (
          <div key={type.id} className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
            <div className="flex flex-col gap-0.5">
              <button onClick={() => handleMove(idx, -1)} disabled={idx === 0} className="text-gray-300 hover:text-gray-500 disabled:opacity-30">
                <ArrowUp size={12} />
              </button>
              <button onClick={() => handleMove(idx, 1)} disabled={idx === types.length - 1} className="text-gray-300 hover:text-gray-500 disabled:opacity-30">
                <ArrowDown size={12} />
              </button>
            </div>

            {editingId === type.id ? (
              <div className="flex-1 grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  value={form.emoji}
                  onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))}
                  className="col-span-1 border rounded px-2 py-1 text-sm text-center"
                  maxLength={4}
                />
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="col-span-4 border rounded px-2 py-1 text-sm"
                />
                <input
                  type="text"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="col-span-5 border rounded px-2 py-1 text-sm"
                />
                <div className="col-span-2 flex gap-1">
                  <button onClick={() => handleUpdate(type.id)} className="text-green-600 hover:text-green-800 text-xs font-medium">
                    ✓
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600 text-xs">
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span className="text-xl w-8 text-center">{type.emoji || '📄'}</span>
                <div className="flex-1">
                  <span className="font-medium text-sm text-gray-900">{type.name}</span>
                  {type.description && (
                    <span className="text-xs text-gray-400 ml-2">— {type.description}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {type.isDefault && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Lock size={10} /> Par défaut
                    </span>
                  )}
                  <button onClick={() => startEdit(type)} className="text-gray-400 hover:text-primary">
                    <Edit size={14} />
                  </button>
                  {!type.isDefault && (
                    <button onClick={() => handleDelete(type.id)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
